import { Stack, TextField } from '@fluentui/react'
import { SendRegular } from '@fluentui/react-icons'
import { useContext, useEffect, useRef, useState } from 'react'

import { Close } from '@mui/icons-material'
import { ChatMessage } from '../../api'
import SheetIcon from '../../assets/csv_icon.svg'
import ErrorFileIcon from '../../assets/error_icon.svg'
import FileIcon from '../../assets/file.svg'
import PDFIcon from '../../assets/pdf_icon.svg'
import PptxIcon from '../../assets/pptx_icon.svg'
import DOCIcon from '../../assets/txt_icon.svg'
import { AppStateContext } from '../../state/AppProvider'
import styles from './QuestionInput.module.css'
import { CustomTooltip } from '../CustomTooltip'
interface Props {
  onSend: (question: ChatMessage['content'], id?: string) => void
  disabled: boolean
  placeholder?: string
  clearOnSend?: boolean
  conversationId?: string
}

const MAX_FILES_COUNT = 20
const MAX_FILE_SIZE_MB = 50
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024 // 50 MB in bytes

export const QuestionInput = ({ onSend, disabled, placeholder, clearOnSend, conversationId }: Props) => {
  const [question, setQuestion] = useState<string>('')
  const [attachedFiles, setAttachedFiles] = useState<Array<{ filename: string; fileType: string; size: number }>>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const appStateContext = useContext(AppStateContext)
  const [isUploading, setIsUploading] = useState(false)
  const [filesWithErrors, setFilesWithErrors] = useState<Array<{ filename: string; fileType: string; size: number }>>(
    [],
  )

  useEffect(() => {
    setAttachedFiles([])
    setFilesWithErrors([])
  }, [conversationId])

  const sendQuestion = () => {
    if (disabled || !question.trim()) {
      return
    }

    const questionTest: ChatMessage['content'] = question.toString()

    if (conversationId && questionTest !== undefined) {
      onSend(questionTest, conversationId)
    } else {
      onSend(questionTest)
    }

    if (clearOnSend) {
      setQuestion('')
    }
  }

  const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
    if (ev.key === 'Enter' && !ev.shiftKey && !(ev.nativeEvent?.isComposing === true)) {
      ev.preventDefault()
      sendQuestion()
    }
  }

  const onQuestionChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setQuestion(newValue || '')
  }

  const totalFileSize = attachedFiles.reduce((sum, file) => sum + file.size, 0)
  const isAttachDisabled = attachedFiles.length >= MAX_FILES_COUNT || totalFileSize >= MAX_FILE_SIZE_BYTES

  const handleAttachFileClick = () => {
    if (!isAttachDisabled) {
      fileInputRef.current?.click()
    }
  }

  const getFileExtension = (fileName: string) => {
    const extension = fileName.split('.').pop()
    switch (extension) {
      case 'pdf':
        return 'PDF'
      case 'doc':
      case 'docx':
        return 'DOC'
      case 'txt':
        return 'Text'
      case 'csv':
      case 'xls':
      case 'xlsx':
        return 'Sheet'
      case 'ppt':
      case 'pptx':
        return 'Pptx'
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'bmp':
      case 'tiff':
      case 'ico':
      case 'webp':
      case 'svg':
      case 'heic':
      case 'heif':
        return 'Image'
      default:
        return 'File'
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsUploading(true)
    const files = event.target.files
    const filesBeingProcessed: Array<{ filename: string; fileType: string; size: number }> = []

    try {
      if (files && files.length > 0 && !isAttachDisabled) {
        const remainingSlots = MAX_FILES_COUNT - attachedFiles.length
        const filesArray = Array.from(files)

        // Calculate total size of new files
        const newFilesTotalSize = filesArray.reduce((sum, file) => sum + file.size, 0)
        const currentTotalSize = totalFileSize

        // Check if adding these files would exceed the limit
        if (currentTotalSize + newFilesTotalSize > MAX_FILE_SIZE_BYTES) {
          appStateContext?.dispatch({
            type: 'SET_SNACKBAR_STATE',
            payload: {
              message: `${MAX_FILE_SIZE_MB} MB limit exceeded. Remove some files or select smaller files.`,
              severity: 'warning',
            },
          })
          // Reset the input
          if (fileInputRef.current) {
            fileInputRef.current.value = ''
          }
          setIsUploading(false)
          return
        } else if (remainingSlots < filesArray.length && filesArray.length > 1) {
          appStateContext?.dispatch({
            type: 'SET_SNACKBAR_STATE',
            payload: {
              message: `You can only upload up to ${MAX_FILES_COUNT} files at a time.`,
              severity: 'warning',
            },
          })
          // Reset the input
          if (fileInputRef.current) {
            fileInputRef.current.value = ''
          }
          setIsUploading(false)
          return
        }

        // Add files that fit within the limit
        const filesToAdd: Array<{ filename: string; fileType: string; size: number }> = []

        for (const file of filesArray) {
          try {
            const fileInfo = {
              filename: file.name,
              fileType: getFileExtension(file.name),
              size: file.size,
            }
            filesToAdd.push(fileInfo)
            filesBeingProcessed.push(fileInfo)
          } catch (fileError) {
            // Track individual file errors
            const fileInfo = {
              filename: file.name,
              fileType: getFileExtension(file.name),
              size: file.size,
            }
            setFilesWithErrors(prev => [...prev, fileInfo])
          }
        }

        if (filesToAdd.length > 0) {
          setAttachedFiles(prev => [...prev, ...filesToAdd])
        }
      }
      // Reset the input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      setIsUploading(false)
    } catch (error) {
      // Add all files that were being processed to filesWithErrors
      if (filesBeingProcessed.length > 0) {
        setFilesWithErrors(prev => [...prev, ...filesBeingProcessed])
      }
      setIsUploading(false)
    }
  }

  const handleRemoveFile = (index: number, isError = false) => {
    try {
      if (isError) {
        setFilesWithErrors(prev => prev.filter((_, i) => i !== index))
      } else {
        setAttachedFiles(prev => prev.filter((_, i) => i !== index))
      }
      appStateContext?.dispatch({
        type: 'SET_SNACKBAR_STATE',
        payload: { message: 'Attachment deleted succesfully.', severity: 'success' },
      })
    } catch (error) {
      appStateContext?.dispatch({
        type: 'SET_SNACKBAR_STATE',
        payload: { message: 'Attachment deletion failed. Try again later.', severity: 'warning' },
      })
    }
  }

  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'PDF':
        return PDFIcon
      case 'DOC':
      case 'Text':
      case 'Image':
        return DOCIcon
      case 'Sheet':
        return SheetIcon
      case 'Pptx':
        return PptxIcon
      default:
        return FileIcon
    }
  }

  const sendQuestionDisabled = disabled || !question.trim() || isUploading

  return (
    <Stack className={styles.questionInputContainer}>
      <Stack className={styles.questionInputInnerContainer}>
        {(attachedFiles.length > 0 || filesWithErrors.length > 0) && (
          <Stack className={styles.questionInputFileAttachmentContainer}>
            {filesWithErrors.map((file, index) => (
              <CustomTooltip
                key={index}
                title={file.filename}
                placement="right-start">
                <div style={{ display: 'inline-block' }}>
                  <Stack className={styles.questionInputFileAttachment}>
                    <div className={styles.questionInputFileIcon}>
                      <div className={styles.questionInputFileIconInner}>
                        <img src={ErrorFileIcon} />
                      </div>
                    </div>
                    <Stack className={styles.questionInputFileInfo}>
                      <div className={styles.questionInputFileName}>{file.filename}</div>
                      <div className={styles.questionInputErrorMessage}>Error uploading the file</div>
                    </Stack>
                    <button
                      className={styles.questionInputFileRemoveButtonError}
                      onClick={() => handleRemoveFile(index, true)}
                      aria-label={`Remove ${file.filename}`}
                      type="button">
                      <Close className={styles.questionInputFileRemoveButtonIconError} />
                    </button>
                  </Stack>
                </div>
              </CustomTooltip>
            ))}
            {attachedFiles.map((file, index) => (
              <CustomTooltip
                key={index}
                title={file.filename}
                placement="right-start">
                <div style={{ display: 'inline-block' }}>
                  <Stack className={styles.questionInputFileAttachment}>
                    <div className={styles.questionInputFileIcon}>
                      <div className={styles.questionInputFileIconInner}>
                        <img src={getFileIcon(file.fileType)} />
                      </div>
                    </div>
                    <Stack className={styles.questionInputFileInfo}>
                      <div className={styles.questionInputFileName}>{file.filename}</div>
                      <div className={styles.questionInputFileType}>{file.fileType}</div>
                    </Stack>
                    <button
                      className={styles.questionInputFileRemoveButton}
                      onClick={() => handleRemoveFile(index)}
                      aria-label={`Remove ${file.filename}`}
                      type="button">
                      <Close className={styles.questionInputFileRemoveButtonIcon} />
                    </button>
                  </Stack>
                </div>
              </CustomTooltip>
            ))}
          </Stack>
        )}
        <Stack className={styles.questionInputTextAreaContainer}>
          <TextField
            className={styles.questionInputTextArea}
            placeholder={placeholder}
            multiline
            resizable={false}
            borderless
            value={question}
            onChange={onQuestionChange}
            onKeyDown={onEnterPress}
          />
        </Stack>
        <div className={styles.questionInputBottomBar}>
          <Stack className={styles.questionInputBottomActions}>
            {/* @TODO: Remove the <div /> below and uncomment when the backend for attaching files is ready */}
            <div />
            {/* <input
              ref={fileInputRef}
              type="file"
              multiple
              disabled={isAttachDisabled}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <img
              src={PaperClipIcon}
              alt="Attach file"
              className={`${styles.questionInputAttachIcon} ${isAttachDisabled ? styles.questionInputAttachIconDisabled : ''}`}
              onClick={handleAttachFileClick}
              role="button"
              tabIndex={isAttachDisabled ? -1 : 0}
              aria-label="Attach file"
              aria-disabled={isAttachDisabled}
              onKeyDown={e => {
                if (!isAttachDisabled && (e.key === 'Enter' || e.key === ' ')) {
                  handleAttachFileClick()
                }
              }}
            /> */}
            <div
              className={styles.questionInputSendButtonContainer}
              role="button"
              tabIndex={0}
              aria-label="Ask question button"
              onClick={sendQuestion}
              onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? sendQuestion() : null)}>
              <SendRegular
                className={
                  sendQuestionDisabled ? styles.questionInputSendButtonDisabled : styles.questionInputSendButton
                }
              />
            </div>
          </Stack>
        </div>
      </Stack>
    </Stack>
  )
}
